<template>
  <div class="collections__edit">
    <HeadSection :title="collectionName" :breadcrumbs="breadcrumbs" permissionOfSection="unified-collection">
      <template #actions>
        <a-col :span="14" class="text-right d-flex justify-content-end">
          <div v-if="editPermission">
            <a-button size="large" class="mr-4" @click="openCollectionSettings"> Settings</a-button>
            <a-button type="primary" size="large" @click="isImportModalOpen = true" class="mr-4"> Import</a-button>
            <a-button type="primary" size="large" @click="isCreateModalOpen = true">
              <a-icon type="plus" /> Add item
            </a-button>
          </div>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" permissionOfSection="unified-collection">
      <div class="d-flex align-items-center justify-content-between mb-4">
        <div style="flex-grow: 1">
          <a-input-search v-model="search" placeholder="Input search text" enter-button @change="getCollectionItems" :loading="loading" />
        </div>
      </div>
      <a-table :columns="columns" :data-source="collectionItems" :rowKey="(record, index) => record.id" :row-selection="rowSelection" :loading="loading"
        :pagination="isPaginationAvailable" @change="changePagination"
        class="collections__table">
        <template slot="created_at" slot-scope="text, record">
          <span>{{ formatDate(record.created_at) }}</span>
        </template>
        <template slot="actions" slot-scope="text, record">
          <div class="row-operations d-flex justify-content-between">
            <a-popconfirm placement="left" title="Are you sure you want to delete this collection item?" :disabled="!editPermission" @confirm="() => deleteCollectionItems(record.id)">
              <a-icon class="font-size-18 text-danger delete-icon"  :class="{ 'text-gray-5 icon-disabled': !editPermission }" style="margin-top: 2px;" type="delete" />
            </a-popconfirm>
          </div>
        </template>
      </a-table>
      <a-popconfirm @confirm="() => deleteCollectionItems()" :disabled="isDeleteItemsDisabled"
        :title="`Are you sure you want to delete ${this.selectedRowKeys.length > 1 ? 'these collection items?' : 'this collection item?'}`"
        placement="bottom" class="mt-3">
        <a-button size="large" :disabled="isDeleteItemsDisabled" :class="{'reverse-danger' : !isDeleteItemsDisabled}" type="danger"> Delete</a-button>
      </a-popconfirm>
    </MainContainer>
    <a-modal v-model="isImportModalOpen" title="Import collection items" :footer="null" width="450px" :destroyOnClose="true">
      <ImportCollectionItemsModal @closeImportModal="isImportModalOpen = false" :collectionId="collectionId" @addNewItems="handleAddCollectionItems" />
    </a-modal>
    <a-modal v-model="isCreateModalOpen" title="Add collection items" :footer="null" width="450px" :destroyOnClose="true">
      <CollectionModal @closeCollectionModal="isCreateModalOpen = false" :collectionId="collectionId" mode="add" @addNewItem="handleAddCollectionItem" />
    </a-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'

import ImportCollectionItemsModal from '../modals/importCollectionItemsModal.vue'
import CollectionModal from '../modals/collectionModal.vue'

import CollectionsService from '@/services/api/apiCollectionsService'

export default {
  name: 'CollectionsList',
  components: {
    HeadSection,
    MainContainer,
    ImportCollectionItemsModal,
    CollectionModal,
  },
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    editPermission: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isImportModalOpen: false,
      isCreateModalOpen: false,
      loading: false,
      collectionItems: null,
      search: '',
      selectedRowKeys: [],
      columns: [
        {
          title: 'ID',
          dataIndex: 'id',
          width: '10%',
          scopedSlots: { customRender: 'id' },
        },
        {
          title: 'Item',
          dataIndex: 'value',
          width: '27%',
        },
        {
          title: 'Created',
          dataIndex: 'created_at',
          width: '15%',
          scopedSlots: { customRender: 'created_at' },
        },
        {
          title: 'Actions',
          width: '4%',
          scopedSlots: { customRender: 'actions' },
        },
      ],
      pagination: {
        pageSize: 15,
        current: 1,
        total: 0,
      },
    }
  },
  computed: {
    ...mapState(['user']),
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    collectionId() {
      return this.$route.params.id
    },
    collectionName() {
      return decodeURIComponent(this.$route.params.name)
    },
    isDeleteItemsDisabled() {
      return !this.editPermission || !this.selectedRowKeys.length
    },
    breadcrumbs() {
      return [
        { name: 'Collections', link: '/profiles/collections' },
        { name: this.collectionName, link: undefined },
      ]
    },
    isPaginationAvailable() {
      return (this.collectionItems && this.collectionItems.length >= 15) || this.pagination.current !== 1 ? this.pagination : false
    },
    rowSelection() {
      return {
        selectedRowKeys: this.selectedRowKeys,
        onChange: this.onSelectChange,
        columnWidth: '3%',
        getCheckboxProps: (record) => ({
          props: {
            disabled: false,
          },
        }),
      }
    },
  },
  async created() {
    await this.getCollectionItems()
  },
  methods: {
    formatDate: function (date) {
      return this.$moment(date).tz('Etc/GMT-3').format('DD.MM.YY HH:mm')
    },
    changePagination(pagination) {
      this.pagination = pagination
      this.getCollectionItems()
    },
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys
    },
    handleAddCollectionItem() {
      this.isCreateModalOpen = false
      this.getCollectionItems()
    },
    handleAddCollectionItems() {
      this.isImportModalOpen = false
      this.getCollectionItems()
    },
    openCollectionSettings() {
      this.$router.push(`/profiles/collections/settings/${this.collectionId}/${this.$route.params.name}`)
    },
    async getCollectionItems() {
      this.loading = true
      try {
        const collectionItemsResponse = await CollectionsService.getCollectionItems(this.pagination.current, this.pagination.pageSize, this.search, this.collectionId)
        this.collectionItems = collectionItemsResponse.data.data
        this.pagination.total = collectionItemsResponse.data.meta.total
      } catch (err) {
        let errorMessage = 'Something went wrong. Please try again later.'

        if (err.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message
        } else if (err.message) {
          errorMessage = err.message
        }

        this.$notification.error({
          message: errorMessage,
        })
        this.loading = false
      } finally {
        this.loading = false
      }
    },
    async deleteCollectionItems(itemId = null) {
      const idsToDelete = itemId ? [itemId] : this.selectedRowKeys
      try {
        const collectionDeleteResponse = await CollectionsService.deleteCollectionItems(this.collectionId, idsToDelete)

        if (collectionDeleteResponse.data?.error) {
          this.$notification.error({
            message: 'Something went wrong',
            description: collectionDeleteResponse.data.error.message,
          })
          return
        }

        if (collectionDeleteResponse.data) {
          this.collectionItems = this.collectionItems.filter(item => !idsToDelete.includes(item.id))
          if (!itemId) {
            this.selectedRowKeys = []
          }
          this.$notification.success({
            message: idsToDelete.length > 1
              ? `${idsToDelete.length} items were successfully deleted`
              : 'The item was successfully deleted',
          })
        }
      } catch (err) {
        let errorMessage = 'Something went wrong. Please try again later.'

        if (err.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message
        } else if (err.message) {
          errorMessage = err.message
        }

        this.$notification.error({
          message: errorMessage,
        })
      }
    },
  },
}
</script>
